import { Box, Skeleton, theme } from "@prismicio/editor-ui"

import { useRepositoryTypes } from "../../../app/services/customTypes"
import {
  useRepository,
  useRepositoryTags
} from "../../../app/services/repository"
import { ArchiveDocumentsButton } from "../../BulkActions/ArchiveDocumentsButton"
import { CopyDocumentsToLocale } from "../../BulkActions/CopyDocumentsToLocale"
import { DeleteDocumentsButton } from "../../BulkActions/DeleteDocumentsButton"
import { PublishDocumentsButton } from "../../BulkActions/PublishDocumentsButton"
import { CreateNewDocumentButton } from "../CreateNewDocumentDialog"
import { FilterDropDown, FilterDropDownBoundary } from "./FilterDropdown"
import { FiltersBar } from "./FiltersBar"
import { useActionBar } from "./useActionBar"
import { getAuthorDisplayName, getFilterType } from "./utils"

interface ActionBarProps {
  tab: "working" | "archived" | "upcoming"
  setModalOpen: (isOpen: boolean) => void
  createEnabled?: boolean
  releaseId?: string
}

export function ActionBar(props: ActionBarProps) {
  const { tab, setModalOpen, createEnabled = true, releaseId } = props

  const { selectedFilters, onFilterSelected, onRemoveFilter } = useActionBar()

  const selectedCustomTypeFilters = getFilterType(selectedFilters, "customType")
  const selectedTagFilters = getFilterType(selectedFilters, "tag")
  const selectedAuthorFilters = getFilterType(selectedFilters, "author")

  return (
    <Box flexDirection="column" gap={8}>
      <Box
        justifyContent="space-between"
        alignItems="center"
        minHeight={filterBarMinHeight}
      >
        <Box gap={16}>
          <FilterDropDownBoundary
            type="customType"
            selectedFilters={selectedCustomTypeFilters}
          >
            <FilterDropDown
              type="customType"
              optionsFetcher={useRepositoryTypesFilters}
              selectedFilters={selectedCustomTypeFilters}
              onFilterSelected={(filterId: string) =>
                onFilterSelected(filterId, "customType")
              }
            />
          </FilterDropDownBoundary>
          <FilterDropDownBoundary
            type="author"
            selectedFilters={selectedAuthorFilters}
          >
            <FilterDropDown
              type="author"
              optionsFetcher={useRepositoryAuthorsFilters}
              selectedFilters={selectedAuthorFilters}
              onFilterSelected={(filterId: string) =>
                onFilterSelected(filterId, "author")
              }
            />
          </FilterDropDownBoundary>
          <FilterDropDownBoundary
            type="tag"
            selectedFilters={selectedTagFilters}
          >
            <FilterDropDown
              type="tag"
              optionsFetcher={useRepositoryTagsFilters}
              selectedFilters={selectedTagFilters}
              onFilterSelected={(filterId: string) =>
                onFilterSelected(filterId, "tag")
              }
            />
          </FilterDropDownBoundary>
        </Box>

        <Box gap={16} alignItems="center">
          <PublishDocumentsButton releaseId={releaseId} />
          {tab !== "archived" && (
            <ArchiveDocumentsButton releaseId={releaseId} />
          )}
          {tab === "working" && <CopyDocumentsToLocale />}
          {tab === "archived" && <DeleteDocumentsButton />}

          {createEnabled && (
            <CreateNewDocumentButtonBasedOnTab
              tab={tab}
              setModalOpen={setModalOpen}
            />
          )}
        </Box>
      </Box>

      {selectedFilters.length > 0 && (
        <FiltersBar
          selectedFilters={selectedFilters}
          onRemoveFilter={onRemoveFilter}
        />
      )}
    </Box>
  )
}

function CreateNewDocumentButtonBasedOnTab(props: ActionBarProps) {
  const { tab, setModalOpen } = props

  if (tab === "working")
    return <CreateNewDocumentButton setModalOpen={setModalOpen} />

  if (tab === "upcoming")
    return (
      <CreateNewDocumentButton
        color="grey"
        size="small"
        setModalOpen={setModalOpen}
      />
    )

  return null
}

export function useRepositoryTypesFilters() {
  const repositoryCustomTypes = useRepositoryTypes()

  return (
    repositoryCustomTypes
      // Filter out custom types that are disabled
      // (status is a boolean field, true means enabled)
      .filter((customType) => customType.status)
      .map((customType) => ({
        label: customType.label,
        id: customType.id,
        key: customType.id
      }))
  )
}

export function useRepositoryTagsFilters() {
  const repositoryCustomTags = useRepositoryTags().results

  return repositoryCustomTags.map((tag) => ({
    label: tag,
    id: tag,
    key: tag
  }))
}

export function useRepositoryAuthorsFilters() {
  const { authors } = useRepository()

  return authors.map((author) => ({
    label: getAuthorDisplayName(author),
    id: author.id,
    key: author.id
  }))
}

// The height of the "Create New" Button.
// This is so the bar doesn't jump in size when going from/to screens that have the Button vs not.
const filterBarMinHeight = theme.size.large

export function ActionBarSkeleton() {
  return (
    <Box justifyContent="space-between">
      <Box gap={16}>
        <Skeleton height={24} width={54} />
        <Skeleton height={24} width={74} />
        <Skeleton height={24} width={54} />
      </Box>
      <Skeleton height={24} width={84} />
    </Box>
  )
}
